export type DashboardAccent =
	| 'danger'
	| 'warning'
	| 'success'
	| 'info'
	| 'primary'
	| 'rose'
	| 'orange'
	| 'blue'
	| 'purple'
	| 'green'
	| 'red';

export const DASHBOARD_THEME_PALETTE: Record<
	string,
	{ light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }
> = {
	purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
	primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
	blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
	info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
	green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
	success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
	orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
	warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
	red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
	danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
	rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
};

/** Legacy bootstrap card-header-* mapping for dashboard widgets. */
export const DASHBOARD_WIDGET_ACCENTS: Record<string, DashboardAccent> = {
	'open-orders': 'danger',
	'open-quotes': 'primary',
	'gap-report': 'primary',
	'needs-bin': 'warning',
	'negative-items': 'success',
	'new-items': 'info',
	'new-customers': 'info',
	'top-customers': 'rose',
	'no-purchase': 'warning',
	'open-credits': 'primary',
	'open-purchase-orders': 'orange',
	'trending-products': 'success',
	'team-leaderboard': 'rose',
	'category-and-lines': 'info',
	'items-received': 'warning',
	'items-sold': 'success',
	'recent-transactions': 'primary',
	'thisoverlast': 'success',
	'brand-report': 'primary',
};

// Cache: widgets bind this through [ngStyle]="themeVars" getters that run on
// every change-detection pass. Returning the same object per prefix+accent
// keeps ngStyle's differ idle instead of re-diffing a fresh object each cycle.
const THEME_VARS_CACHE = new Map<string, Record<string, string>>();

export function buildDashboardThemeVars(
	prefix: string,
	colorKey: DashboardAccent | string
): Record<string, string> {
	const cacheKey = prefix + ':' + colorKey;
	const cached = THEME_VARS_CACHE.get(cacheKey);
	if (cached) {
		return cached;
	}
	const p = DASHBOARD_THEME_PALETTE[colorKey] || DASHBOARD_THEME_PALETTE['blue'];
	const rgb  = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
	const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
	const vars: Record<string, string> = {
		[`--${prefix}-accent`]:        rgb(p.base),
		[`--${prefix}-accent-dark`]:   rgb(p.dark),
		[`--${prefix}-accent-light`]:  rgb(p.light),
		[`--${prefix}-accent-tint`]:   rgba(p.base, 0.07),
		[`--${prefix}-accent-tint-2`]: rgba(p.base, 0.14),
		[`--${prefix}-accent-border`]: rgba(p.base, 0.20),
		[`--${prefix}-accent-bg`]:     rgba(p.light, 0.16),
	};
	THEME_VARS_CACHE.set(cacheKey, vars);
	return vars;
}
